/**
 * useSourcePicker — стандартный выбор источника файла у ЛЮБОЙ кнопки загрузки (тот же UploadPickMenu, что у слотов
 * генератора): «Загрузить фото/видео» (с компьютера), «Из моих генераций» (GalleryPickerModal), «Персонаж / Локация /
 * Предмет». В отличие от useUploadPickerMenu не завязан на GenerateContext: что делать с файлом или ассетом, решает
 * вызывающий (колбэки при открытии). Файл из «Моих генераций» приходит обычным File — тем же путём, что с компьютера
 * (проверки и загрузка у вызывающего не раздваиваются).
 *
 * Использование: const picker = useSourcePicker(); кнопка — onClick={(e) => picker.open(e.currentTarget, {...})};
 * {picker.nodes} — один раз в JSX.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { AssetKind } from '../../../api/assets'
import { showToast } from '../../../components/Toast'
import UploadPickMenu from '../components/UploadPickMenu'
import GalleryPickerModal from '../components/GalleryPickerModal'
import { fetchImageAsFile } from '../utils/uploadHelpers'

export interface SourcePickOptions {
	kind: 'images' | 'videos'
	/** Файл с компьютера или из «Моих генераций». */
	onFile: (file: File) => void
	/** Несколько файлов с компьютера разом (картинки-референсы): тогда приходят сюда, а не в onFile. */
	onFiles?: (files: File[]) => void
	/** Пункты «Персонаж / Локация / Предмет» (только у фото); без колбэка их нет. */
	onScene?: (kind: AssetKind) => void
	/** Какие из них показать (по умолчанию все три). */
	sceneKinds?: AssetKind[]
	/** Типы файлов с компьютера; по умолчанию — картинки или mp4/mov/webm. */
	accept?: string
	/** Класс меню (оно порталом в body): своя тема у тёмной студии. */
	className?: string
	/** Выбран любой пункт меню — например, закрыть шторку, из которой меню открыли (пока выбора нет, она открыта). */
	onChoose?: () => void
}

// Ширина меню — как у слотов генератора (useUploadPickerMenu): по ней меню центрируется над кнопкой у края экрана.
const PICKER_W = 190
const MARGIN = 8
const VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/webm'

export function useSourcePicker() {
	const [menu, setMenu] = useState<{ opts: SourcePickOptions; bottom: number; left: number } | null>(null)
	const [gallery, setGallery] = useState<SourcePickOptions | null>(null)
	// Выбор с компьютера приходит после закрытия меню — колбэк держим отдельно от его состояния.
	const fileOpts = useRef<SourcePickOptions | null>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	// Кнопка, у которой открыто меню: повторное нажатие на неё закрывает меню, а не открывает заново.
	const anchorRef = useRef<HTMLElement | null>(null)

	const open = useCallback((buttonEl: HTMLElement, opts: SourcePickOptions) => {
		if (anchorRef.current === buttonEl) { anchorRef.current = null; setMenu(null); return }
		const rect = buttonEl.getBoundingClientRect()
		const ideal = rect.left + rect.width / 2
		const left = Math.max(MARGIN + PICKER_W / 2, Math.min(window.innerWidth - MARGIN - PICKER_W / 2, ideal))
		anchorRef.current = buttonEl
		setMenu({ opts, bottom: window.innerHeight - rect.top + 8, left })
	}, [])

	const close = useCallback(() => { anchorRef.current = null; setMenu(null) }, [])

	// Закрыть кликом мимо и Esc. Нажатие на саму кнопку меню не «мимо» — её клик закроет меню (open выше).
	// Esc закрывает только меню: окно под ним (шторка, выбор локации) остаётся — ловим его раньше всех (window,
	// фаза захвата), дальше событие не идёт.
	useEffect(() => {
		if (!menu) return
		const onDown = (e: MouseEvent) => {
			const t = e.target as HTMLElement
			if (!t.closest?.('.upload-picker') && !anchorRef.current?.contains(t)) close()
		}
		const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close() } }
		document.addEventListener('mousedown', onDown)
		window.addEventListener('keydown', onKey, true)
		return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey, true) }
	}, [menu, close])

	// Esc в «Моих генерациях» закрывает только их: окно, из которого выбирали (загрузка своего, выбор локации,
	// шторка), остаётся — Esc перехватываем раньше всех и закрываем галерею сами.
	useEffect(() => {
		if (!gallery) return
		const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setGallery(null) } }
		window.addEventListener('keydown', onKey, true)
		return () => window.removeEventListener('keydown', onKey, true)
	}, [gallery])

	const pickFile = () => {
		if (!menu || !inputRef.current) return
		fileOpts.current = menu.opts
		inputRef.current.accept = menu.opts.accept ?? (menu.opts.kind === 'videos' ? VIDEO_ACCEPT : 'image/*')
		inputRef.current.multiple = !!menu.opts.onFiles
		inputRef.current.click()
		menu.opts.onChoose?.()
		close()
	}

	const pickGallery = () => {
		if (!menu) return
		setGallery(menu.opts)
		menu.opts.onChoose?.()
		close()
	}

	const onGallerySelect = async (url: string) => {
		const opts = gallery
		setGallery(null)
		if (!opts) return
		const file = await fetchImageAsFile(url, 'gallery')
		if (file) opts.onFile(file)
		else showToast('Файл из ваших генераций не открылся — попробуйте ещё раз', 'error')
	}

	const nodes = (
		<>
			<input ref={inputRef} type="file" hidden
				onChange={(e) => {
					const files = Array.from(e.target.files ?? [])
					const opts = fileOpts.current
					if (opts && files.length) { if (opts.onFiles) opts.onFiles(files); else opts.onFile(files[0]) }
					e.target.value = ''
				}} />
			{menu && createPortal(
				<UploadPickMenu
					kind={menu.opts.kind}
					className={menu.opts.className}
					style={{ position: 'fixed', bottom: menu.bottom, left: menu.left }}
					onPickFile={pickFile}
					onPickGallery={pickGallery}
					onPickScene={menu.opts.onScene && menu.opts.kind === 'images'
						? (k) => { const { onScene, onChoose } = menu.opts; onChoose?.(); close(); onScene?.(k) }
						: undefined}
					sceneKinds={menu.opts.sceneKinds}
				/>,
				document.body,
			)}
			{gallery && <GalleryPickerModal initialTab={gallery.kind} onSelect={(url) => void onGallerySelect(url)} onClose={() => setGallery(null)} />}
		</>
	)

	return { open, nodes }
}
